import React, { useState, useEffect } from 'react';
import { Button } from '../../../ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../../../ui/card';
import { QuizQuestion, QuizAnswer } from '../../../../types/types';
import { Badge } from '../../../ui/badge';

interface QuestionDisplayProps {
  question: QuizQuestion;
  questionNumber: number;
  totalQuestions: number;
  onSubmitAnswer: (questionId: string, selectedOption: number, timeToAnswer: number) => void;
  playerID: string;
  playerAnswers: Record<string, Record<string, QuizAnswer>>;
  disabled: boolean;
}

/**
 * Component to display a quiz question with multiple-choice options
 */
const QuestionDisplay: React.FC<QuestionDisplayProps> = ({
  question,
  questionNumber,
  totalQuestions,
  onSubmitAnswer,
  playerID,
  playerAnswers,
  disabled,
}) => {
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [startTime] = useState<number>(Date.now());
  const [submitted, setSubmitted] = useState<boolean>(false);

  // Check if the current player has already answered this question
  const currentPlayerAnswer = playerAnswers[playerID]?.[question.id];
  const hasCurrentPlayerAnswered = !!currentPlayerAnswer;

  // Track which players have answered for display purposes only
  const playersWhoAnswered = Object.keys(playerAnswers).filter(
    player => playerAnswers[player]?.[question.id],
  );

  // Update submitted state when current player's answers change
  useEffect(() => {
    if (hasCurrentPlayerAnswered) {
      setSubmitted(true);
    }
  }, [hasCurrentPlayerAnswered]);

  const handleOptionSelect = (optionIndex: number) => {
    if (!hasCurrentPlayerAnswered && !submitted) {
      setSelectedOption(optionIndex);
    }
  };

  const handleSubmit = () => {
    if (selectedOption !== null && !submitted) {
      const timeToAnswer = Date.now() - startTime;
      onSubmitAnswer(question.id, selectedOption, timeToAnswer);
      setSubmitted(true);
    }
  };

  // When the question changes, reset selection and submission state
  useEffect(() => {
    setSelectedOption(null);
    setSubmitted(hasCurrentPlayerAnswered);
  }, [question.id, hasCurrentPlayerAnswered]);

  return (
    <Card className='border-2 border-blue-100'>
      <CardHeader className='bg-blue-50'>
        <div className='flex justify-between items-center'>
          <div>
            <CardTitle className='text-lg'>{question.question}</CardTitle>
            <CardDescription>
              Question {questionNumber} of {totalQuestions}
            </CardDescription>
          </div>
          <div className='flex flex-col items-end gap-1'>
            <Badge>{question.category}</Badge>
            <span className='text-xs text-gray-500'>
              {playersWhoAnswered.length} / {Object.keys(playerAnswers).length} players answered
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent className='pt-4'>
        <div className='space-y-2'>
          {question.options.map((option: string, index: number) => {
            const isSelected = selectedOption === index;
            const isPlayerPreviousSelection = currentPlayerAnswer?.selectedOption === index;
            const isCorrectAnswer = hasCurrentPlayerAnswered && question.correctAnswer === index;

            let optionClass = 'p-3 border rounded-md cursor-pointer text-left transition-colors';

            if (hasCurrentPlayerAnswered || submitted) {
              // Show correct/incorrect after answer is submitted - only for the current player
              if (isCorrectAnswer) {
                optionClass += ' bg-green-100 border-green-500';
              } else if (isPlayerPreviousSelection) {
                optionClass += ' bg-red-100 border-red-500';
              } else {
                optionClass += ' opacity-70';
              }
            } else {
              // Selection state before submitting
              optionClass += isSelected ? ' bg-blue-100 border-blue-500' : ' hover:bg-gray-50';
            }

            return (
              <div key={index} className={optionClass} onClick={() => handleOptionSelect(index)}>
                <div className='flex items-start'>
                  <div className='w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-full border mr-2 bg-white'>
                    {String.fromCharCode(65 + index)}
                  </div>
                  <span>{option}</span>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
      <CardFooter className='flex justify-between'>
        <div>
          <Badge variant='outline'>Points: {question.points}</Badge>
        </div>
        <Button
          onClick={handleSubmit}
          disabled={selectedOption === null || hasCurrentPlayerAnswered || submitted}>
          {hasCurrentPlayerAnswered || submitted ? 'Submitted' : 'Submit Answer'}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default QuestionDisplay;
